import { useQuery } from 'react-query';
import isEmpty from 'lodash/isEmpty';

import useAbortController from '../../../hooks/useAbortController';
import AffordableSetUpService from '../../../services/affordableSetUpService';
import MasterRDProjectRentalTypesService from '../../../services/MasterRDProjectRentalTypesService';
import StateAffordableProgramConfigService from '../../../services/stateAffordableProgramConfigService';
/**
 *
 *
 * @param {{
 *   organizationId: string,
 *   propertyId: string,
 *   state: string // Must be formatted as `<country>-<state>` ex: 'US-CA'
 * }} {
 *   organizationId,
 *   propertyId,
 *   state
 * }
 * @return {*}
 */
export const useFetchPassbookRatesByState = ({
  organizationId,
  propertyId,
  state,
}: {
  organizationId: string,
  propertyId: string,
  state: string,
}) => {
  const affordableSetUpService = new AffordableSetUpService();
  const abortControllerOptions = useAbortController();
  const queryKey = [
    'useFetchPassbookRatesByState',
    organizationId,
    propertyId,
    state,
  ];
  const { data, isLoading } = useQuery(
    queryKey,
    () =>
      affordableSetUpService.getAllPassbookRatesByState(
        organizationId,
        propertyId,
        state,
        abortControllerOptions,
      ),
    {
      enabled:
        !isEmpty(organizationId) && !isEmpty(propertyId) && !isEmpty(state),
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
  );

  return {
    data,
    isLoading,
  };
};

export const useFetchMasterRDProjectRentalTypes = () => {
  const service = new MasterRDProjectRentalTypesService();

  const queryKey = ['useFetchMasterRDProjectRentalTypes'];

  const { data, isLoading } = useQuery(queryKey, () => service.getAll(), {
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  const formattedOptions =
    data?.map((item) => ({
      text: item.name,
      value: item.id,
    })) ?? [];

  return {
    data: formattedOptions,
    isLoading,
  };
};

export const useFetchStateAffordableProgramConfigsByPropertyId = ({
  propertyId,
}: {
  propertyId: string,
}) => {
  const service = new StateAffordableProgramConfigService();
  const abortControllerOptions = useAbortController();

  const queryKey = [
    'useFetchStateAffordableProgramConfigsByPropertyId',
    propertyId,
  ];

  const { data, isLoading } = useQuery(
    queryKey,
    () => service.getAllByPropertyId(propertyId, abortControllerOptions),
    {
      enabled: !isEmpty(propertyId ?? ''),
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
  );

  return {
    data,
    isLoading,
  };
};
